import React from "react";
import { AlertCircle, RotateCcw } from "lucide-react";
import { captureException } from "../../lib/sentryLazy";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional custom fallback message */
  message?: string;
  /** Optional callback when user clicks retry */
  onRetry?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ---------------------------------------------------------------------------
// Chunk-load error detection (stale deploy cache)
// ---------------------------------------------------------------------------

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes("Failed to fetch dynamically imported") ||
    error.message.includes("Loading chunk") ||
    error.message.includes("Loading CSS chunk") ||
    error.name === "ChunkLoadError"
  );
}

/** SessionStorage key used to prevent infinite reload loops. */
const CHUNK_RELOAD_KEY = "fp_chunk_reload";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Error Boundary — catches unhandled runtime errors in the React tree
 * and shows a friendly recovery UI instead of a blank crash screen.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <PropertyAnalysisDashboard />
 *   </ErrorBoundary>
 *
 * React class component (required — hooks cannot catch render errors).
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Stale-deploy chunk errors: force one hard reload to pick up new HTML
    if (isChunkLoadError(error)) {
      if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
        window.location.reload();
        return;
      }
      // Already tried reloading — fall through to error UI
    }

    // Report to Sentry with full component stack
    captureException(error, (scope) => {
      scope.setTag("error_context", "error_boundary");
      scope.setExtra("componentStack", errorInfo.componentStack);
    });

    console.error(
      "[ErrorBoundary] Caught render error:",
      error.message,
      "\nComponent stack:",
      errorInfo.componentStack
    );
  }

  handleRetry = (): void => {
    // Full page reload — the only safe recovery from a chunk load failure
    // or crashed component tree. Resetting React state re-renders the same
    // broken tree, which escalates to React Router's unhandled error page.
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      // Already retried once — don't loop. Navigate home instead.
      window.location.href = "/";
      return;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = "/";
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const message =
      this.props.message ||
      "Something went wrong loading this property. Please try again.";

    return (
      <div className="min-h-screen bg-fp-slate-100 font-inter flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-red-200 shadow-sm p-8 max-w-lg w-full text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-fp-navy-900 mb-2">
            Unexpected Error
          </h2>
          <p className="text-slate-500 text-sm mb-6">{message}</p>

          {/* Error detail (dev only) */}
          {import.meta.env.DEV && this.state.error && (
            <pre className="text-left text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-3 mb-6 overflow-x-auto max-h-40">
              {this.state.error.message}
            </pre>
          )}

          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleRetry}
              className="bg-fp-emerald-500 hover:bg-fp-emerald-600 text-white font-bold py-3 px-6 rounded-lg shadow-sm transition-colors flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Try Again
            </button>
            <button
              onClick={this.handleGoHome}
              className="bg-white hover:bg-slate-50 text-fp-navy-900 font-medium py-3 px-6 rounded-lg border border-slate-200 transition-colors"
            >
              Go Home
            </button>
          </div>

          <p className="text-xs text-slate-400 mt-6">
            If this keeps happening, please contact support@fairpath.app
          </p>
        </div>
      </div>
    );
  }
}
